import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  RotateCcw, 
  Settings2, 
  Type, 
  Palette, 
  Check, 
  Moon, 
  Sun, 
  Monitor, 
  Trees, 
  Sunset,
  Droplets,
  Zap,
  Gem,
  Sparkles,
  Circle,
  Square
} from "lucide-react";
import { useTheme, ThemePreset } from "@/contexts/ThemeContext";
import { ResponsiveModal, ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ThemeDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const PRESETS: { id: ThemePreset; label: string; icon: React.ElementType; color: string }[] = [
  { id: 'light', label: 'Light', icon: Sun, color: 'bg-white border-gray-200 text-gray-900' },
  { id: 'dark', label: 'Dark', icon: Moon, color: 'bg-zinc-950 border-zinc-800 text-white' },
  { id: 'ocean', label: 'Ocean', icon: Droplets, color: 'bg-slate-900 border-slate-700 text-sky-400' },
  { id: 'sunset', label: 'Sunset', icon: Sunset, color: 'bg-orange-950 border-orange-800 text-orange-200' },
  { id: 'forest', label: 'Forest', icon: Trees, color: 'bg-green-950 border-green-800 text-green-200' },
  { id: 'purple', label: 'Royal', icon: Palette, color: 'bg-violet-950 border-violet-800 text-violet-200' },
  { id: 'minimal', label: 'Minimal', icon: Monitor, color: 'bg-gray-50 border-gray-200 text-gray-900' },
  { id: 'cyber', label: 'Cyber', icon: Zap, color: 'bg-slate-950 border-cyan-800 text-cyan-300' },
  { id: 'amber', label: 'Amber', icon: Gem, color: 'bg-amber-950 border-amber-800 text-amber-200' },
  { id: 'emerald', label: 'Emerald', icon: Sparkles, color: 'bg-emerald-950 border-emerald-800 text-emerald-200' },
  { id: 'cobalt', label: 'Cobalt', icon: Circle, color: 'bg-blue-950 border-blue-800 text-blue-300' },
  { id: 'charcoal', label: 'Charcoal', icon: Square, color: 'bg-neutral-950 border-neutral-800 text-neutral-300' },
];

export function ThemeDialog({ isOpen, setIsOpen }: ThemeDialogProps) {
  const { theme, setThemePreset, setContentScale, setCustomColor, resetTheme } = useTheme();

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    const onPopState = (e: PopStateEvent) => {
      setIsOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen, setIsOpen]);

  const header = (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-primary/20 rounded-lg">
          <Settings2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Appearance</h2>
          <p className="text-xs text-foreground/80">Customize your viewing experience</p>
        </div>
      </div>
    </div>
  );

  return (
    <ResponsiveModal open={isOpen} setOpen={setIsOpen} direction="bottom" drawerClassName="z-[60]" header={header}>
      <ResponsiveModalContent className="w-full bg-card">
        <div className="flex flex-col h-full">

          <div className="p-4 space-y-8 pb-20">
            {/* Quick Presets */}
            <section>
              <h3 className="text-sm font-medium text-foreground mb-3 uppercase tracking-wider">Theme Presets</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => setThemePreset(preset.id)}
                    className={cn(
                      "relative group flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200",
                      theme.preset === preset.id 
                        ? "border-primary ring-2 ring-primary/20 shadow-lg" 
                        : "border-transparent hover:border-border bg-card/50",
                      preset.color
                    )}
                  >
                    {theme.preset === preset.id && (
                      <div className="absolute top-2 right-2 p-1 bg-primary rounded-full text-primary-foreground">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <preset.icon className={cn("w-6 h-6 mb-2", theme.preset === preset.id ? "scale-110" : "opacity-70")} />
                    <span className="text-sm font-medium">{preset.label}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Content Scale */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-foreground uppercase tracking-wider">Text Size</h3>
                <span className="text-sm font-bold bg-card px-2 py-1 rounded text-foreground">
                  {Math.round(theme.contentScale * 100)}%
                </span>
              </div>
              <div className="bg-card/50 p-6 rounded-xl border border-border">
                <div className="flex items-center gap-4">
                  <Type className="w-4 h-4 text-foreground/80" />
                  <Slider
                    value={[theme.contentScale]}
                    onValueChange={(val) => setContentScale(val[0])}
                    min={0.8}
                    max={1.3}
                    step={0.05}
                    className="flex-1"
                  />
                  <Type className="w-6 h-6 text-foreground" />
                </div>
                <div className="mt-4 p-4 bg-card rounded-lg border border-border text-center">
                  <p className="text-foreground/80">
                    This is how your content will look like.
                  </p>
                </div>
              </div>
            </section>

            {/* Advanced Colors */}
            <section>
               <h3 className="text-sm font-medium text-foreground mb-3 uppercase tracking-wider">Fine Tuning</h3>
               <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="colors" className="border-border">
                  <AccordionTrigger className="hover:no-underline py-4 px-4 bg-card/50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4 text-foreground/80" />
                      <span className="text-foreground">Custom Colors</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 px-2">
                    <div className="grid grid-cols-1 gap-4">
                      
                      {/* Backgrounds */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-foreground">Backgrounds</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Page Background</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.background}
                                onChange={(e) => setCustomColor('background', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                              <span className="text-xs font-mono text-foreground/80">{theme.colors.background}</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Card Background</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.card}
                                onChange={(e) => setCustomColor('card', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                              <span className="text-xs font-mono text-foreground/80">{theme.colors.card}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Text */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-foreground">Text & Accents</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Main Text</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.foreground}
                                onChange={(e) => setCustomColor('foreground', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                              <span className="text-xs font-mono text-foreground/80">{theme.colors.foreground}</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Primary Color</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.primary}
                                onChange={(e) => setCustomColor('primary', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                              <span className="text-xs font-mono text-foreground/80">{theme.colors.primary}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Selection */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-foreground">Highlighting</label>
                         <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Selection Bg</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.selectionColor}
                                onChange={(e) => setCustomColor('selectionColor', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <span className="text-xs text-foreground/80">Selection Text</span>
                            <div className="flex items-center gap-2 bg-card/50 p-2 rounded-lg border border-border">
                              <input 
                                type="color" 
                                value={theme.colors.selectionTextColor}
                                onChange={(e) => setCustomColor('selectionTextColor', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-none p-0 bg-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-auto p-4 border-t border-border/60 bg-background/60 backdrop-blur-md sticky bottom-0">
             <Button
              onClick={resetTheme}
              variant="outline"
              className="w-full h-12 border-border text-foreground hover:bg-background/80 hover:text-foreground/90 transition-colors duration-200"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Default
            </Button>
            <div className="h-2" />
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
